<template>
  <v-layout column justify-space-between class="manage-rulecollection white">
    <div>
      <!-- Title and Actions -->
      <v-flex xs12 class="py-0">
        <v-toolbar color="secondary" class="elevation-0">
          <!-- Back Button -->
          <v-btn icon flat class="primary--text text--lighten-5" @click="$router.go(-1)">
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <v-toolbar-title class="white--text subheading ttu">
            <router-link :to="{ name: 'MetadataRulesSearch' }" class="white--text link dim">
              {{ messages.label.METADATA_RULES }}
            </router-link>
            / <span v-if="isEditMode">{{ messages.label.EDIT_RULE_COLLECTION }}</span>
            <span v-else>{{ messages.label.CREATE_RULE_COLLECTION }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
      </v-flex>
      <!-- Main Content -->
      <v-flex xs12>
        <v-card flat>
          <!-- Header with tabs and actions -->
          <v-card-title class="grey lighten-4 px-3 py-0">
            <v-tabs v-model="activeTab" @change="ensureWindow" color="grey lighten-4">
              <v-tabs-slider color="accent"></v-tabs-slider>
              <v-tab v-for="item in tabItems" :key="item" class="secondary--text">
                {{ item }}
              </v-tab>
              <v-spacer></v-spacer>
            </v-tabs>
            <v-spacer></v-spacer>
            <v-btn color="primary" round @click="showTestRuleModal()" v-if="isEditMode">
              {{ messages.label.TEST }}
            </v-btn>
          </v-card-title>
          <!-- Main content of rule (info, conditions, actions) -->
          <v-card-text class="pt-1">
            <!-- Loading indicator -->
            <v-progress-linear color="accent" v-show="dataLoading" height="2" :indeterminate="true">
            </v-progress-linear>
            <!-- Error Alert -->
            <v-flex xs12 v-if="errorAlert">
              <v-alert v-model="errorAlert" dismissible type="error">
                {{ errorMessage }}
              </v-alert>
            </v-flex>
            <v-tabs-items v-model="activeTab" ref="window">
              <v-tab-item>
                <RuleCollectionInfo />
              </v-tab-item>
              <v-tab-item>
                <RuleCollectionRuleSets />
              </v-tab-item>
              <v-tab-item v-if="isEditMode">
                <JSONView :json="currentRuleCollectionJSON" />
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-flex>
      <!-- Rule Test Dialog -->
      <MetadataRuleTest v-model="ruleTestDialog" :rule-obj="ruleObjectToTest" />
      <!-- Snackbar -->
      <v-flex xs12>
        <v-snackbar :bottom="true" :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
          {{ snackbar.message }}
          <v-btn flat color="secondary" @click.native="snackbar.show = false">
            {{ messages.label.OK }}
          </v-btn>
        </v-snackbar>
      </v-flex>
    </div>
    <!-- Card actions -->
    <v-card-actions class="grey lighten-4 px-3">
      <v-btn color="grey lighten-1 white--text" @click="cancel()">
        {{ messages.label.CANCEL }}
      </v-btn>
      <v-btn color="primary" v-if="isEditMode" :disabled="createOrUpdateInProgress" :loading="createOrUpdateInProgress"
        @click="validateForUpdateRuleCollection()">
        {{ messages.label.SAVE }}
      </v-btn>
      <v-btn color="primary" v-else :disabled="createOrUpdateInProgress" :loading="createOrUpdateInProgress"
        @click="validateForCreateRuleCollection()">
        {{ messages.label.CREATE }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn color="error" v-if="isEditMode" @click="showDeleteDialog()">
        <span>{{ messages.label.DELETE }}</span>
      </v-btn>
      <!-- Delete Dialog -->
      <v-dialog v-model="deleteDialog" width="500">
        <v-card>
          <v-card-title class="subheading ttu">
            {{ messages.label.DELETE }}
          </v-card-title>
          <v-card-text>
            <!-- Error Alert -->
            <v-flex xs12 v-if="deleteErrorAlert">
              <v-alert v-model="deleteErrorAlert" type="error">
                {{ deleteErrorMessage }}
              </v-alert>
            </v-flex>
            <p>{{ messages.info.DELETE_RULE_1 }} <b>{{ currentRuleCollection.title }}</b> ? </p>
            <p v-html="messages.info.DELETE_RULE_2"></p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey lighten-1 white--text" @click="cancelDelete">
              {{ messages.label.CANCEL }}
            </v-btn>
            <v-btn color="error" :disabled="deletingRule" :loading="deletingRule" @click="deleteRuleCollection()">
              {{ messages.label.YES }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-actions>
  </v-layout>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from "vuex"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import * as utils from "@/Shared/utils"
import AppService from "@/AppService"
import RuleQuickView from "@/MetadataRules/Rule/RuleQuickView"
import RuleCollectionRuleSets from "@/MetadataRules/RuleCollection/RuleCollectionRuleSets"
import RuleCollectionInfo from "@/MetadataRules/RuleCollection/RuleCollectionInfo"
import JSONView from "@/MetadataRules/JSONView"
import MetadataRuleTest from "@/MetadataRules/MetadataRuleTest"
import messages from "@/MetadataRules/messages.json"
import { SET_RULECOLLECTION_RULESETS } from "@/store/constants/mutation-types"
import { GET_MPX_PARENT_ACCOUNT } from "@/store/constants/action-types"
import { MVATS_WEB_SERVICE_URL, RULE_MGMT_DATA_SERVICE_URL } from "@/store/constants/getter-types"

export default {
  name: "ManageRuleCollection",
  components: {
    RuleCollectionInfo,
    RuleCollectionRuleSets,
    RuleQuickView,
    JSONView,
    MetadataRuleTest
  },
  data() {
    return {
      activeTab: null,
      isEditMode: false,
      errorAlert: false,
      errorMessage: "",
      ruleObjectToTest: {},
      ruleTestDialog: false,
      validationStatus: {
        info: false,
        rules: true
      },
      ruleCollectionAction: "",
      ruleCollectionInfoData: {},
      currentRuleCollection: {},
      currentRuleCollectionJSON: {},
      snackbar: {
        show: false,
        timeout: 5000,
        message: "",
        color: "error"
      },
      dataLoading: false,
      createOrUpdateInProgress: false,
      deleteDialog: false,
      deletingRule: false,
      deleteErrorAlert: false,
      deleteErrorMessage: "",
      windowTimeout: null
    }
  },
  watch: {
    validationStatus: {
      handler(newValue) {
        if (newValue.info === true && newValue.rules === true) {
          switch (this.ruleCollectionAction) {
            case "create":
              this.createRuleCollection()
              break
            case "update":
              this.updateRuleCollection()
              break
          }
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapState("metadataRules", {
      ruleCollectionRuleSetsList: state => state.ruleCollectionRuleSetsList
    }),
    ...mapGetters([
      MVATS_WEB_SERVICE_URL, RULE_MGMT_DATA_SERVICE_URL
    ]),
    tabItems() {
      if (this.isEditMode) {
        return ["INFO",
          "RULE SETS",
          "{ JSON }"]
      } else {
        return ["INFO",
          "RULE SETS"]
      }
    }
  },
  methods: {
    ...mapMutations("metadataRules", [
      SET_RULECOLLECTION_RULESETS
    ]),
    ...mapActions([
      GET_MPX_PARENT_ACCOUNT
    ]),
    /**
     * validateForUpdateRuleCollection - Begin validation for update ruleset
     */
    validateForUpdateRuleCollection() {
      this.ruleCollectionAction = "update"
      this.validateAll()
    },

    /**
     * validateForCreateRuleCollection - Begin validation for create ruleset
     */
    validateForCreateRuleCollection() {
      this.ruleCollectionAction = "create"
      this.validateAll()
    },

    /**
     * resetValidationStatus - Reset validation status
     */
    resetValidationStatus() {
      this.validationStatus = {
        info: false,
        rules: false
      }
    },

    /**
     * resetData - Reset data
     */
    resetData() {
      if (this.$lodash.isNull(this.activeTab)) {
        this.activeTab = 0
      }
      this.isEditMode = false
      this.ruleCollectionAction = ""
      this.ruleCollectionInfoData = {}
      this.currentRuleCollection = {}
      this.currentRuleCollectionJSON = {}
      this.errorAlert = false
      this.errorMessage = ""
      this.SET_RULECOLLECTION_RULESETS([])
      this.resetValidationStatus()
    },

    /**
     * validateAll - Validate ruleset info and rules
     */
    validateAll() {
      MetadataRulesService.$emit("event:validateRuleCollectionInfo")
      MetadataRulesService.$emit("event:validateRuleCollectionRuleSets")
    },

    /**
     * validateRuleCollectionInfo - Handles validation of ruleset info view
     */
    validateRuleCollectionInfo(result, ruleCollectionInfoData) {
      this.validationStatus.info = result
      this.ruleCollectionInfoData = ruleCollectionInfoData
      if (!this.validationStatus.info) {
        this.activeTab = 0 // Show Info tab
      }
    },

    /**
     * validateRuleCollectionRuleSets - Handles validation of rules view
     */
    validateRuleCollectionRuleSets(result) {
      this.validationStatus.rules = result
      if (!this.validationStatus.rules) {
        this.activeTab = 1 // Show Info tab
      }
    },

    /**
     * createRuleCollection - Create new ruleset
     */
    createRuleCollection() {
      this.createOrUpdateInProgress = true
      let ruleCollectionData = {
        title: this.ruleCollectionInfoData.title,
        type: this.ruleCollectionInfoData.type,
        disabled: !this.ruleCollectionInfoData.enabled,
        description: this.ruleCollectionInfoData.description,
        secondaryOwner: this.ruleCollectionInfoData.secondaryOwner,
        items: this.getRuleCollectionRuleSetGUIDs()
      }
      metadataRulesDataService
        .createRuleCollection(ruleCollectionData,
          this.ruleCollectionInfoData.ownerId,
          this.RULE_MGMT_DATA_SERVICE_URL)
        .then((response) => {
          let ruleCollectionId = response.data.id.split("/").pop()
          let ownerId = response.data.ownerId
          this.showSnackBar("success", this.messages.info.RULECOLLECTION_CREATE_SUCCESS)
          this.$router.replace({
            name: "ManageRuleCollection",
            params: { id: ruleCollectionId },
            query: { account: ownerId }
          })
        })
        .catch((error) => {
          this.resetValidationStatus()
          this.showError(error)
        })
        .finally(() => {
          this.createOrUpdateInProgress = false
        })
    },

    /**
     * updateRuleCollection - Update existing ruleset
     */
    updateRuleCollection() {
      this.createOrUpdateInProgress = true
      // Update current rule data
      this.currentRuleCollection.title = this.ruleCollectionInfoData.title
      this.currentRuleCollection.type = this.ruleCollectionInfoData.type
      this.currentRuleCollection.disabled = !this.ruleCollectionInfoData.enabled
      this.currentRuleCollection.description = this.ruleCollectionInfoData.description
      this.currentRuleCollection.secondaryOwner = this.ruleCollectionInfoData.secondaryOwner
      this.currentRuleCollection.items = this.getRuleCollectionRuleSetGUIDs()
      metadataRulesDataService
        .updateRuleCollection(this.currentRuleCollection,
          this.ruleCollectionInfoData.ownerId,
          this.RULE_MGMT_DATA_SERVICE_URL)
        .then(() => {
          this.showSnackBar("success", this.messages.info.RULECOLLECTION_UPDATE_SUCCESS)
          let ruleCollectionId = this.currentRuleCollection.id.split("/").pop()
          let ownerId = this.currentRuleCollection.ownerId
          this.loadRuleCollectionData(ruleCollectionId, ownerId)
          this.resetValidationStatus()
        })
        .catch((error) => {
          this.showError(error)
        })
        .finally(() => {
          this.resetValidationStatus()
          this.createOrUpdateInProgress = false
        })
    },

    /**
     * getRuleCollectionRuleSetGUIDs - Retrieves just the rule ids required for create / update
     */
    getRuleCollectionRuleSetGUIDs() {
      return this.$lodash.map(this.ruleCollectionRuleSetsList, "guid")
    },

    /**
     * retreiveRuleCollectionDetails - Retrieve rule collection details with given id
     * @param {string} ruleCollectionId
     * @param {string} account
     */
    retreiveRuleCollectionDetails(ruleCollectionId, account) {
      this.currentRuleCollection = {}
      this.currentRuleCollectionJSON = {}
      let queryObject = {
        byId: ruleCollectionId,
        account: account
      }
      return metadataRulesDataService
        .getRuleCollection(queryObject,
          this.RULE_MGMT_DATA_SERVICE_URL)
        .then((response) => {
          // RuleSet data from "data" service
          if (response.data.entries.length > 0) {
            this.currentRuleCollection = response.data.entries[0]
            return this.currentRuleCollection
          } else {
            // TODO: Redirect to 404
            return Promise.reject(
              new Error(`Could not find rule collection with id: ${ruleCollectionId}`)
            )
          }
        })
        .then((response) => {
          // NOTE: As list of rule objects of a ruleset are available
          // through web service, call web service to retrieve rules of this ruleset
          let queryObject = {
            ruleCollectionGuid: response.guid,
            account: account,
            secondaryOwner: response.secondaryOwner
          }
          return metadataRulesDataService
            .getRuleCollectionDetails(queryObject, this.MVATS_WEB_SERVICE_URL)
            .then((response) => {
              this.currentRuleCollectionJSON = this.$lodash.cloneDeep(response.data)
              let ruleCollectionRuleSets = MetadataRulesService.addClientOnlyFields(response.data.ruleSetDetails)
              this.$lodash.forEach(ruleCollectionRuleSets, (ruleSet, index) => {
                ruleCollectionRuleSets[index].rules = MetadataRulesService.addClientOnlyFields(ruleSet.rules)
              })
              this.SET_RULECOLLECTION_RULESETS(ruleCollectionRuleSets)
            }).catch((error) => {
              return Promise.reject(error)
            })
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    },

    /**
     * loadRuleCollectionData - Takes care of loading rule collection data in the views
     * @param {string} ruleId
     * @param {string} account
     */
    loadRuleCollectionData(ruleCollectionId, account) {
      this.dataLoading = true
      this.resetData()
      return this.GET_MPX_PARENT_ACCOUNT()
        .then(() => {
          if (!this.$lodash.isUndefined(ruleCollectionId)) {
            // Edit mode
            this.isEditMode = true
            return this.retreiveRuleCollectionDetails(ruleCollectionId, account)
              .then((response) => {
                // Rule collection data retrieved
                MetadataRulesService.$emit("event:ruleCollectionDataRetrieved", this.currentRuleCollection)
                return response
              }).catch((error) => {
                this.showError(error)
              }).finally(() => {
                this.dataLoading = false
              })
          } else {
            // Create mode
            this.isEditMode = false
            MetadataRulesService.$emit("event:ruleCollectionDataRetrieved")
            this.dataLoading = false
            return Promise.resolve({})
          }
        })
    },

    /**
     * showSnackBar - Show snackbar message
     * @param {string} color
     * @param {string} message
     */
    showSnackBar(color, message) {
      this.snackbar.show = true
      this.snackbar.color = color
      this.snackbar.message = message
    },

    /**
     * showError - Handles displaying error
     * @param {object} error
     */
    showError(error) {
      let isServerError = utils.isMPXError(error)
      let isClientError = utils.isClientError(error)
      if (isServerError) {
        this.errorAlert = true
        this.errorMessage = error.response.data.description
      } else if (isClientError) {
        this.errorAlert = true
        this.errorMessage = error.message
      }
    },
    /**
     * cancel - Cancel out of rule create / edit view
     */
    cancel() {
      this.$router.push({ name: "MetadataRulesSearch" })
    },
    /**
    * showDeleteDialog -
    */
    showDeleteDialog() {
      this.deleteDialog = true
    },

    /**
     * cancelDelete - close delete dialog
     */
    cancelDelete() {
      this.deleteErrorAlert = false
      this.deleteErrorMessage = ""
      this.deleteDialog = false
    },

    /**
     * deleteRuleCollection - Delete selected rule / ruleset / rule collection
     */
    deleteRuleCollection() {
      this.deletingRule = true
      let ruleCollectionId = this.currentRuleCollection.id.split("/").pop()
      metadataRulesDataService
        .deleteRuleCollection(ruleCollectionId,
          this.RULE_MGMT_DATA_SERVICE_URL)
        .then(() => {
          this.deleteDialog = false
          // Show Snackbar
          this.snackbar.show = true
          this.snackbar.color = "success"
          this.snackbar.message = this.messages.info.DELETE_SUCCESS + this.currentRuleCollection.title
          // route user to search screen
          this.$router.push({ name: "MetadataRulesSearch" })
        }).catch((error) => {
          let isServerError = utils.isMPXError(error)
          if (isServerError) {
            this.deleteErrorAlert = true
            this.deleteErrorMessage = error.response.data.description
          }
        })
        .finally(() => {
          this.deletingRule = false
        })
    },

    /**
     * showTestRuleModal
     */
    showTestRuleModal() {
      this.ruleObjectToTest = this.currentRuleCollection
      this.ruleTestDialog = true
    },

    /**
     * NOTE: Fix for auto height adjust issue of v-tabs
     * FIXME: Once fix is available, update vuetify version
     */
    ensureWindow() {
      clearTimeout(this.windowTimeout)
      this.windowTimeout = setTimeout(() => {
        this.$refs.window.internalHeight = null
        this.$refs.window.isActive = false
      }, 1000)
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     * */
    MetadataRulesService.$on("event:ruleCollectionInfoValidation", this.validateRuleCollectionInfo)
    MetadataRulesService.$on("event:ruleCollectionRuleSetsValidation", this.validateRuleCollectionRuleSets)
    AppService.$on("event:preIngestServerError", () => {
      this.showSnackBar("error", this.messages.error.SERVER_ERROR)
    })
  },

  /**
   * Triggered on first entry in to the route /metadata-rules/rule-set
   * @param {object} to
   * @param {object} from
   * @param {Function} next
   */
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.loadRuleCollectionData(to.params.id, to.query.account)
    })
  },

  /**
   * Triggered on update to the route /metadata-rules/rule-set
   * @param {object} to
   * @param {object} from
   * @param {Function} next
   */
  beforeRouteUpdate(to, from, next) {
    this.loadRuleCollectionData(to.params.id, to.query.account)
      .then(() => {
        next()
      })
  },
  beforeDestroy() {
    MetadataRulesService.$off("event:ruleCollectionInfoValidation", this.validateRuleCollectionInfo)
    MetadataRulesService.$off("event:ruleCollectionRuleSetsValidation", this.validateRuleCollectionRuleSets)
  }
}
</script>

<style scoped>
.manage-rulecollection .tabs-extension {
  height: 48px;
}

.manage-rulecollection {
  min-height: 90%;
}
</style>



// WEBPACK FOOTER //
// src/MetadataRules/RuleCollection/ManageRuleCollection.vue